import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import { Card, PageHeader, GhostBtn, SectionTitle, ConfirmModal } from "../components/common/Ui";

// Fixed list of Home Circle categories shown on the storefront.
// Admin can only change / remove the circle's image — labels & routes stay fixed.
const DEFAULT_CIRCLES = [
  { key: "diary",     label: "Diary",          href: "/Dailyplanne" },
  { key: "photobook", label: "Photobook",      href: "/photobook" },
  { key: "album",     label:  "Sketches",         href: "/bold-bright" },
  { key: "journal",   label: "Journal",        href: "/gratitude-journals" },
  { key: "notebook",  label: "Notebook",       href: "/notebooks" },
  { key: "notepad",   label: "Notepad",        href: "/list-notepads" },
  { key: "calendar",  label: "Table Calendar", href: "/travel-planner" },
  { key: "planner",    label: "Planner",    href: "/Productivityplanner" },
  { key: "Gift Hampers",    label: "Gift Hampers",    href: "/gift-hampers" },
  
];

const HomeCircle = () => {
  const [circles, setCircles] = useState(
    DEFAULT_CIRCLES.map((c) => ({ ...c, id: null, image: null }))
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeCircleKey, setActiveCircleKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Load existing circles from the backend ──
  useEffect(() => {
    setLoading(true);
    setLoadError("");
    apiFetch("/home/categories.php")
      .then((res) => {
        const rows = res.data || [];
        setCircles(
          DEFAULT_CIRCLES.map((c) => {
            const match = rows.find((r) => r.label === c.label);
            return match
              ? { ...c, id: match.id, image: match.image_url }
              : { ...c, id: null, image: null };
          })
        );
      })
      .catch((err) => setLoadError(err.message || "Failed to load circles."))
      .finally(() => setLoading(false));
  }, []);

  const openPicker = (key) => {
    setActiveCircleKey(key);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setActiveCircleKey(null);
  };

  // Pick a photo -> upload to backend (create if this circle has no row yet, else update)
  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeCircleKey) return;

    const circle = circles.find((c) => c.key === activeCircleKey);
    if (!circle) return;

    setUploading(true);
    try {
      const fd = new FormData();
      if (circle.id) fd.append("id", circle.id);
      fd.append("label", circle.label);
      fd.append("link_path", circle.href);
      fd.append("image", file);

      const res = await apiFetch("/home/categories.php", { method: "POST", body: fd });
      const newId = res.data?.id ?? circle.id;
      const previewUrl = URL.createObjectURL(file);

      setCircles((prev) =>
        prev.map((c) => (c.key === activeCircleKey ? { ...c, id: newId, image: previewUrl } : c))
      );
      closePicker();
    } catch (err) {
      alert(err.message || "Failed to upload photo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const confirmDelete = async () => {
    const circle = circles.find((c) => c.key === deleteTarget);
    if (!circle?.id) {
      setDeleteTarget(null);
      return;
    }

    setDeleting(true);
    try {
      await apiFetch(`/home/categories.php?id=${circle.id}`, { method: "DELETE" });
      setCircles((prev) =>
        prev.map((c) => (c.key === deleteTarget ? { ...c, id: null, image: null } : c))
      );
    } catch (err) {
      alert(err.message || "Failed to delete image.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="p-4 sm:p-6" style={{ background: T.bg, minHeight: "100vh" }}>
      <PageHeader
        title="Home Circle"
        subtitle="Manage the category circle images shown on the homepage"
      />

      <Card className="p-4 sm:p-6">
        <SectionTitle>Category Circles</SectionTitle>

        {loading ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.textSec }}>
            Loading circles…
          </p>
        ) : loadError ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.danger }}>
            {loadError}
          </p>
        ) : (
          <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-4 gap-5 sm:gap-6">
            {circles.map((c) => (
              <div key={c.key} className="flex flex-col items-center gap-2.5">
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                  style={{ background: T.bgSoft, border: `1.5px solid ${T.border}` }}
                >
                  {c.image ? (
                    <img src={c.image} alt={c.label} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] text-center px-2" style={{ color: T.textSec }}>
                      No image
                    </span>
                  )}
                </div>

                <span
                  className="text-[12px] font-medium text-center"
                  style={{ color: T.textPri, fontFamily: T.sans }}
                >
                  {c.label}
                </span>

                <div className="flex items-center gap-1.5">
                  <GhostBtn onClick={() => openPicker(c.key)}>Edit</GhostBtn>
                  {c.image && (
                    <GhostBtn onClick={() => setDeleteTarget(c.key)}>Delete</GhostBtn>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Gallery picker modal ── */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={!uploading ? closePicker : undefined}
        >
          <div
            className="rounded-3xl w-full max-w-sm p-6 text-center"
            style={{ background: T.card }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-4xl mb-3">🖼️</div>
            <h3
              className="text-[16px] font-bold mb-2"
              style={{ color: T.textPri, fontFamily: T.serif }}
            >
              Choose a photo
            </h3>
            <p className="text-[12px] mb-6" style={{ color: T.textSec }}>
              Pick an image from your gallery for this circle.
            </p>

            <label
              className="flex items-center justify-center gap-2 rounded-xl py-3 text-[12px] font-semibold cursor-pointer transition-all mb-3"
              style={{ border: `1.5px dashed ${T.gold}`, color: T.gold, opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? "Uploading…" : "+ Choose from gallery"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFilePick}
                disabled={uploading}
              />
            </label>

            <button
              onClick={closePicker}
              disabled={uploading}
              className="w-full py-2.5 rounded-xl text-[13px] font-medium border"
              style={{ borderColor: T.border, color: T.textSec }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      {deleteTarget && (
        <ConfirmModal
          title="Remove image?"
          message="This circle will go back to showing no image until you pick a new one."
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          disabled={deleting}
        />
      )}
    </div>
  );
};

export default HomeCircle;